import React, { useEffect, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import AnimatedHero from "../Components/AnimatedHero";
import PageTransition from "../Components/PageTransition";
import { db } from "../firebase-config";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./CSS/About.css";
import Loader from "../Components/Loader";

// Core values
const values = [
  { icon: "bi-heart-fill", label: "Compassion" },
  { icon: "bi-people-fill", label: "Community" },
  { icon: "bi-lightbulb-fill", label: "Innovation" },
  { icon: "bi-globe", label: "Inclusivity" }
];

// Utility: split comma-separated categories into array
const splitCategories = (str) =>
  str ? str.split(",").map((c) => c.trim().toLowerCase()).filter(Boolean) : [];

// Check if two arrays have any common element
const hasOverlap = (arr1, arr2) => {
  const set2 = new Set(arr2.map((c) => c.toLowerCase()));
  return arr1.some((c) => set2.has(c.toLowerCase()));
};

// Fetch gallery images matching any section category
async function fetchImagesByCategories(sectionCategories) {
  if (!sectionCategories || sectionCategories.length === 0) return [];
  const snapshot = await getDocs(collection(db, "gallery"));
  const secCats = sectionCategories.map((c) => c.toLowerCase());
  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter((item) => Array.isArray(item.category) && hasOverlap(secCats, item.category));
}

// Slideshow Component
function Slideshow({ images, height = 260, delay = 2900 }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!images || images.length < 2) return;

    const timer = setTimeout(() => setIndex((i) => (i + 1) % images.length), delay);
    return () => clearTimeout(timer);
  }, [index, images, delay]);

  if (!images || images.length === 0) return null;

  return (
    <div
      style={{ position: "relative", width: "100%", minHeight: height, maxWidth: 380, margin: "0 auto" }}
    >
      {images.map((img, i) => (
        <img
          key={img.id || i}
          src={img.imageUrl}
          alt={img.title || ""}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height,
            objectFit: "cover",
            boxShadow: "0 2px 10px #0d6efd22",
            opacity: i === index ? 1 : 0,
            transition: "opacity 0.9s",
          }}
        />
      ))}
    </div>
  );
}

export default function About() {
  const navigate = useNavigate();

  const [team, setTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);

  const [content, setContent] = useState({
    orgProfile: "",
    vision: "",
    registeredOn: "21.09.2022",
    address: `GRJ BUILDING,\nNeithala,\nErattakulam (PO),\nPara, Elappully,\nPalakkad – 678622`,
  });
  const [contentLoading, setContentLoading] = useState(true);

  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);

  // Fetch About Content
  useEffect(() => {
    let isMounted = true;
    const fetchContent = async () => {
      try {
        const snap = await getDoc(doc(db, "aboutContent", "main"));
        if (isMounted && snap.exists()) setContent((prev) => ({ ...prev, ...snap.data() }));
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted) setContentLoading(false);
      }
    };
    fetchContent();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch Team
  useEffect(() => {
    let isMounted = true;
    const fetchTeam = async () => {
      try {
        const snapshot = await getDocs(collection(db, "team"));
        let data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        data.sort(
          (a, b) =>
            (a.rNo ?? 9999) - (b.rNo ?? 9999) || (a.cNo ?? 9999) - (b.cNo ?? 9999)
        );
        const grouped = data.reduce((acc, member) => {
          const row = member.rNo ?? "others";
          if (!acc[row]) acc[row] = [];
          acc[row].push(member);
          return acc;
        }, {});
        const sortedRows = Object.keys(grouped)
          .sort((a, b) => Number(a) - Number(b))
          .map((row) => ({ rowNumber: row, members: grouped[row] }));
        if (isMounted) setTeam(sortedRows);
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted) setLoadingTeam(false);
      }
    };
    fetchTeam();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch Sections and Images
  useEffect(() => {
    let isMounted = true;
    const fetchSections = async () => {
      try {
        const snapshot = await getDocs(collection(db, "aboutSections"));
        const secData = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const sec = { id: docSnap.id, ...docSnap.data(), images: [] };
            const categories = splitCategories(sec.category);
            if (categories.length > 0)
              sec.images = await fetchImagesByCategories(categories);
            return sec;
          })
        );
        if (isMounted) setSections(secData);
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted) setSectionsLoading(false);
      }
    };
    fetchSections();
    return () => {
      isMounted = false;
    };
  }, []);

  if (contentLoading || sectionsLoading)
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}>
      <Loader />
    </div>
  );


  return (
    <PageTransition className="about-page bg-light">
      {/* Hero */}
      <AnimatedHero
        title={
          <>
            <span style={{ fontWeight: "bold", letterSpacing: 1 }}>DARVIK</span>
            <br />
            <span style={{ fontWeight: "normal" }}>FOUNDATION</span>
          </>
        }
        subtitle="Empowering communities, nurturing nature, and preserving culture for a sustainable future."
        className="about-hero"
        overlayColor="rgba(14,77,109,0.15)"
      />

      {/* Profile */}
      <section className="container py-5">
        <div className="row g-5 align-items-center">
          <div className="col-md-7">
            <h2 className="mb-3 primary">Organization Profile</h2>
            <ul className="list-unstyled">
              <li>
                <strong>Registered on:</strong> {content.registeredOn}
              </li>
              <li>
                <strong>Address:</strong>
                <br />
                {content.address.split("\n").map((line, idx) => (
                  <span key={idx}>
                    {line}
                    <br />
                  </span>
                ))}
              </li>
            </ul>
            <div dangerouslySetInnerHTML={{ __html: content.orgProfile }} />
          </div>
          <div className="col-md-5 text-center">
            <img
              src="/logodarvik.png"
              alt="Logo"
              className="img-fluid"
              style={{ maxWidth: 320 }}
            />
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="background py-5">
        <div className="container text-center">
          <h2 className="mb-4 primary">Our Vision</h2>
          <div dangerouslySetInnerHTML={{ __html: content.vision }} />
        </div>
      </section>

      {/* Core Values */}
      <section className="container py-5">
        <h2 className="mb-4 text-center primary">Our Core Values</h2>
        <div className="row justify-content-center g-4 ">
          {values.map((val, idx) => (
            <div className="col-6 col-md-3 text-center" key={idx}>
              <div className="card h-100 d-flex flex-column align-items-center">
                <div className="mb-2 hover-icon-container" style={{ fontSize: 36 }}>
                  <i className={`bi ${val.icon}`}></i>
                </div>
                <span className="fw-semibold">{val.label}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Dynamic Sections */}
      {sections.map((sec, idx) => (
        <section className="py-5 container" key={sec.id || idx}>
          <div className="row align-items-center g-4">
            <div className={sec.images?.length > 0 ? "col-lg-7" : "col-12"}>
              <h2 className="mb-3 primary">{sec.title}</h2>
              <div dangerouslySetInnerHTML={{ __html: sec.content }}></div>
            </div>
            {sec.images?.length > 0 && (
              <div className="col-lg-5 text-center">
                <Slideshow images={sec.images} height={260} delay={3000} />
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Team */}
      <section className="container-fluid py-5">
        <h2 className="mb-4 text-center primary">Meet Our Team</h2>
        {loadingTeam ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : (
          team.map((row, idx) => (
            <div
              key={row.rowNumber}
              className={`row justify-content-center g-4 ${idx < team.length - 1 ? "mb-5" : ""}`}
            >
              {row.members.map((member, mIdx) => (
                <div
                  key={mIdx}
                  className={`d-flex justify-content-center ${
                    row.rowNumber === "1"
                      ? "col-12 col-sm-8 col-md-6 col-lg-4"
                      : row.rowNumber === "2"
                      ? "col-6 col-sm-4 col-md-2"
                      : row.rowNumber === "3"
                      ? "col-6 col-sm-4 col-md-3"
                      : "col-6 col-sm-4 col-md-3"
                  }`}
                >
                  <div
                    className="team-card bg-light rounded shadow-sm p-4 text-center team-card-hover"
                    onClick={() =>
                      navigate(`/team/${encodeURIComponent(member.name)}`, {
                        state: { member, from: "about" },
                      })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={member.img}
                      alt={member.name}
                      className="rounded-circle mb-3 team-img"
                    />
                    <h6 className="mb-1">{member.name}</h6>
                    <div className="text-primary mb-1 small">{member.role}</div>

                    {/* Show bio only for Row 1 (head) */}
                    {member.bio && row.rowNumber === "1" && (
                      <p className="small secondary mb-2">{member.bio}</p>
                    )}

                    {/* Social Media Icons (only if links exist) */}
                    <div className="d-flex justify-content-center gap-3 mt-2">
                      {member.social?.facebook && (
                        <a
                          href={member.social.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-facebook text-primary"></i>
                        </a>
                      )}
                      {member.social?.instagram && (
                        <a
                          href={member.social.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-instagram text-danger"></i>
                        </a>
                      )}
                      {member.social?.linkedin && (
                        <a
                          href={member.social.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-linkedin text-primary"></i>
                        </a>
                      )}
                      {member.social?.twitter && (
                        <a
                          href={member.social.twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-twitter text-info"></i>
                        </a>
                      )}
                      {member.social?.youtube && (
                        <a
                          href={member.social.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-youtube text-danger"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </section>

      {/* Call to Action */}
      <section
        className="py-5 text-white text-center position-relative"
        style={{ background: `linear-gradient(90deg, var(--accent-opaced) 60%, var(--primary) 100%)` }}
      >
        <div className="container position-relative z-2">
          <h2 className="mb-3">Join Us in Making a Difference</h2>
          <p className="text-dark">
            Darvik Foundation invites individuals, volunteers, schools, and organizations to collaborate and strengthen
            our efforts.
          </p>
          <a href="/contact" className="btn btn-secondary btn-lg px-4 py-2 fw-semibold shadow">
            Contact Us
          </a>
        </div>
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ opacity: 0.15, background: "url('/logodarvik.png') center/contain no-repeat" }}
        ></div>
      </section>
    </PageTransition>
  );
}
